import { createFeatureSelector, createSelector } from '@ngrx/store';
import moment from 'moment';
import { selectHideTimesheetPersonnelSubArea, selectIsTimesheetDisabled } from '../../config/selectors/config.selector';
import { selectSelectedUser } from '../../user-master/selectors/user-master.selector';
import { spanEpoch } from '../../../signal-form-rules/chronologically-ordered.rule';
import {
    TIMESHEET_COLLECTION_FEATURE_KEY,
    TimesheetCollectionState,
    TimesheetState,
} from '../timesheet-collection.state';

export const selectTimesheetFeature = createFeatureSelector<TimesheetCollectionState>(TIMESHEET_COLLECTION_FEATURE_KEY);

// --- Raw serializable slices -------------------------------------------------

/**
 * Projection persisted under the single `timesheetSlice` key by the generic persistence layer:
 * the two timesheets only, without the transient status/error/integration fields.
 */
export const selectTimesheets = createSelector(selectTimesheetFeature, (state) => state.timesheets);
export const selectTimesheetStatus = createSelector(selectTimesheetFeature, (state) => state.status);
export const selectTimesheetError = createSelector(selectTimesheetFeature, (state) => state.error);
export const selectTimesheetIntegration = createSelector(selectTimesheetFeature, (state) => state.integration);
export const selectSelectedTimesheetId = createSelector(selectTimesheetFeature, (state) => state.selectedTimesheetId);

// --- Rehydrated rich models (memoized) ---------------------------------------
// Rehydration only re-runs when the underlying JSON reference changes, so repeated reads share one
// model instance instead of rebuilding it every time.

/**
 * Returns the first non-submitted timesheet (=the current timesheet). By convention, there can be at most one at a time.
 */
export const selectCurrentTimesheet = createSelector(selectTimesheets, (timesheets): TimesheetState | undefined =>
    timesheets.find((t) => !t.isSubmitted),
);

/**
 * Returns all submitted timesheets
 */
export const selectSubmittedTimesheets = createSelector(selectTimesheets, (timesheets): TimesheetState[] =>
    timesheets.filter((t) => t.isSubmitted),
);

/**
 * Returns the most recent submitted timesheet; ordering is done in the reducer
 */
export const selectSubmittedTimesheet = createSelector(
    selectSubmittedTimesheets,
    (timesheets): TimesheetState | undefined => timesheets[0],
);

export const selectCurrentStartOfWork = createSelector(selectCurrentTimesheet, (timesheet): moment.Moment =>
    timesheet?.startOfWork ? moment(timesheet?.startOfWork) : moment.invalid(),
);

// --- Timesheet picking -------------------------------------------------------

/**
 * All timesheets newest first: the open one leads — a fresh timesheet has an empty
 * `startOfWork` and would otherwise sort last — followed by the submitted ones by
 * descending start of work.
 */
export const selectTimesheetsNewestFirst = createSelector(selectTimesheets, (timesheets): TimesheetState[] =>
    timesheets.toSorted((left, right) => {
        if (left.isSubmitted !== right.isSubmitted) {
            return left.isSubmitted ? 1 : -1;
        }
        return right.startOfWork.localeCompare(left.startOfWork);
    }),
);

/** What the timesheet picker needs to label and order its options. */
export type TimesheetSummary = Pick<TimesheetState, 'timesheetId' | 'startOfWork' | 'endOfWork' | 'isSubmitted'>;

export const selectTimesheetSummaries = createSelector(selectTimesheetsNewestFirst, (timesheets): TimesheetSummary[] =>
    timesheets.map(({ timesheetId, startOfWork, endOfWork, isSubmitted }) => ({
        timesheetId,
        startOfWork,
        endOfWork,
        isSubmitted,
    })),
);

/**
 * The timesheet the UI shows. Falls back to the newest one whenever no selection has been
 * made or the selected id is gone — a rehydrate can replace the whole array, so the
 * selection is resolved on read rather than corrected on write.
 */
export const selectSelectedTimesheet = createSelector(
    selectTimesheetsNewestFirst,
    selectSelectedTimesheetId,
    (timesheets, selectedTimesheetId): TimesheetState | undefined =>
        timesheets.find((timesheet) => timesheet.timesheetId === selectedTimesheetId) ?? timesheets[0],
);

/** A submitted timesheet is shown read-only; `true` while no timesheet is available at all. */
export const selectSelectedTimesheetReadonly = createSelector(
    selectSelectedTimesheet,
    (timesheet): boolean => timesheet?.isSubmitted ?? true,
);

/**
 * The latest `endOfWork` any submitted timesheet accounted for, or `''` when there is nothing to
 * measure the timesheet being edited against.
 *
 * Feeds `notEarlierThan` on the open timesheet's `startOfWork`: a technician's day may not begin
 * before an already-submitted day ended, since two timesheets claiming the same time would each be
 * booked for it in full.
 *
 * `''` whenever there is no open timesheet to validate against a submitted one: no timesheet is
 * selected at all, or the selected timesheet is itself submitted. A submitted timesheet is shown
 * read-only and can never be sent, so there is nothing to validate; and measuring a historical sheet
 * against a *later* submitted one — or against itself — would mark a read-only page with an error
 * nobody can act on.
 *
 * Compared by parsed epoch rather than `localeCompare`, since these are ISO instants that need not
 * share a timezone offset — see {@link spanEpoch}. A submitted timesheet with an unset or
 * unparsable `endOfWork` is skipped rather than treated as the latest.
 */
export const selectLatestSubmittedEndOfWork = createSelector(
    selectSubmittedTimesheets,
    selectSelectedTimesheet,
    (submittedTimesheets, selectedTimesheet): string => {
        if (!selectedTimesheet || selectedTimesheet.isSubmitted) {
            return '';
        }

        return submittedTimesheets.reduce((latest, timesheet) => {
            const candidateEpoch = spanEpoch(timesheet.endOfWork);
            if (candidateEpoch === undefined) {
                return latest;
            }

            const latestEpoch = spanEpoch(latest);
            return latestEpoch === undefined || candidateEpoch > latestEpoch ? timesheet.endOfWork : latest;
        }, '');
    },
);

// --- Feature availability (cross-slice) --------------------------------------

/**
 * Whether the technician's personnel sub-area appears on the config's semicolon-separated hide list,
 * compared case-insensitively. Users without a sub-area are never hidden.
 */
const selectHideTimesheetForPersonnelSubArea = createSelector(
    selectSelectedUser,
    selectHideTimesheetPersonnelSubArea,
    (user, hiddenSubAreas): boolean => {
        const personnelSubArea = user?.personnelSubArea?.trim();
        if (!personnelSubArea) {
            return false;
        }
        return hiddenSubAreas
            .split(';')
            .map((subArea) => subArea.trim().toLowerCase())
            .filter(Boolean)
            .includes(personnelSubArea.toLowerCase());
    },
);

/**
 * Whether the timesheet feature is available at all: on unless the config disables it outright or
 * hides it for the technician's personnel sub-area.
 */
export const selectIsTimesheetFeatureEnabled = createSelector(
    selectIsTimesheetDisabled,
    selectHideTimesheetForPersonnelSubArea,
    (timesheetDisabled, hiddenForSubArea): boolean => !timesheetDisabled && !hiddenForSubArea,
);
